import { useState } from 'react';
import type { Signer } from '@cef-ai/signer';
import { Box, PanelDialog } from '@cere/cere-design-system';
import type { Widget } from '@/lib/manifest/types';
import { WidgetEmbed } from '@/src/components/WidgetEmbed';
import { WidgetTabStrip } from '@/src/components/WidgetTabStrip';

interface Props {
  widgets: Widget[];
  /** Widget signer (the Cere wallet's signingSigner) — bridged into the iframe. */
  signer: Signer | null;
  cdnBaseUrl?: string;
  /** Widget to open on — usually the tab active inline when Expand was clicked. */
  initialWidgetId: string;
  onClose: () => void;
}

/**
 * Near-fullscreen view of a single widget, with a tab strip to switch between
 * an agent's widgets in place. Reuses WidgetEmbed wholesale (iframe + URL
 * safety gate + sign bridge) — this component only owns the overlay chrome and
 * its own active-tab state, seeded from the caller so switching here does not
 * disturb the inline block behind it.
 */
export function WidgetExpandModal({ widgets, signer, cdnBaseUrl, initialWidgetId, onClose }: Props) {
  const [activeId, setActiveId] = useState(initialWidgetId);
  const active = widgets.find((x) => x.id === activeId) ?? widgets[0]!; // callers only open with widgets.length > 0

  return (
    <PanelDialog
      open
      onClose={onClose}
      side="center"
      width="xl"
      transition="fade"
      hideHeader
      ariaLabel={`${active.name?.trim() || active.id} — expanded`}
      paperSx={{
        width: 'calc(100vw - 32px)',
        height: 'calc(100vh - 32px)',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 32px)',
        borderRadius: '16px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Single flex-column root: PanelDialog wraps children in a display:block
          box, which would break the body's flex:1 height chain. This root is
          height:100% + flex column so the body (and its iframe) fill.
          No close button here — PanelDialog's hideHeader renders its own,
          floated top-right; a second one is the double-X bug. */}
      <div className="vault-widget-expand">
        {widgets.length > 1 && (
          <Box className="vault-widget-expand__head">
            <WidgetTabStrip widgets={widgets} activeId={active.id} onSelect={setActiveId} />
          </Box>
        )}
        <Box className="vault-widget-expand__body">
          <WidgetEmbed widget={active} signer={signer} cdnBaseUrl={cdnBaseUrl} />
        </Box>
      </div>
    </PanelDialog>
  );
}
